import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { BootstrapQuestion } from "@/interfaces/v2Analytics";
import type { Rollup } from "@/interfaces/questions";
import { updateQuestionOptionColor } from "@/store/slices/questions/questions.thunk";

export interface QuestionsCatalogState {
  /**
   * The exclusive-questions catalog from the bootstrap embed
   * (`clients[0].questions`). null until bootstrap resolves, or when the
   * embed is absent server-side — the EQ picker shows "No Questions
   * Available" in that case.
   */
  catalog: BootstrapQuestion[] | null;
  /**
   * Session-only color customizations, applied optimistically when the
   * (legacy, write-only) customize call is dispatched. Keys:
   * `option-<registry_question_option.id>` / `band-<band id>`.
   * Bootstrap carries no colors yet, so these last for the session only;
   * once the backend adds `color` to the embed they become an interim
   * overlay until the next bootstrap refresh.
   */
  colorOverrides: Record<string, string>;
  /**
   * Session-only replacement of a question's USER rollups after a
   * `questions/rollups/save` round-trip (the save response is authoritative
   * for the question's user rollups). Keyed by questionKey (= code); value
   * is in the legacy `Rollup` shape the transformer selector emits.
   * Bootstrap is authoritative again next session.
   */
  userRollupsOverride: Record<string, Rollup[]>;
}

const initialState: QuestionsCatalogState = {
  catalog: null,
  colorOverrides: {},
  userRollupsOverride: {},
};

const questionsCatalogSlice = createSlice({
  name: "questionsCatalog",
  initialState,
  reducers: {
    setQuestionsCatalog: (
      state,
      action: PayloadAction<BootstrapQuestion[] | null>,
    ) => {
      state.catalog = action.payload;
      // New bootstrap data is authoritative — drop stale session overlays.
      state.colorOverrides = {};
      state.userRollupsOverride = {};
    },
    setUserRollupsOverride: (
      state,
      action: PayloadAction<{ questionKey: string; rollups: Rollup[] }>,
    ) => {
      state.userRollupsOverride[action.payload.questionKey] =
        action.payload.rollups;
    },
  },
  extraReducers: (builder) => {
    // Optimistic: apply the recolor immediately; the legacy PUT persists it
    // server-side but is never read back until bootstrap carries colors.
    builder.addCase(updateQuestionOptionColor.pending, (state, action) => {
      const { entityId, color, entityType } = action.meta.arg;
      const key =
        entityType === "QUESTION_OPTION"
          ? `option-${entityId}`
          : `band-${entityId}`;
      state.colorOverrides[key] = color;
    });
  },
});

export const { setQuestionsCatalog, setUserRollupsOverride } =
  questionsCatalogSlice.actions;
export default questionsCatalogSlice.reducer;
